import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, Clock } from "lucide-react";
import { imgSrc } from "@/lib/api";

export default function OrderConfirmation() {
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);

  // order details arrive via sessionStorage right after payment — URL stays clean at /thankyou
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("rl_last_order");
      if (raw) setOrder(JSON.parse(raw));
      else setError(true);
    } catch {
      setError(true);
    }
  }, []);

  const money = (v) => order?.currency === "USD" ? `$${Number(v).toLocaleString("en-US")}` : `₹${Number(v).toLocaleString("en-IN")}`;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FDF8F0] px-5 py-16 text-[#2B1E12]" data-testid="order-confirmation-page">
      <div className="w-full max-w-lg rounded-md border border-[#E9DFC8] bg-white p-8 shadow-[0_30px_80px_-40px_rgba(143,110,29,0.3)] sm:p-10">
        {error ? (
          <div className="text-center">
            <XCircle className="mx-auto mb-4 h-12 w-12 text-red-500" />
            <h1 className="font-display text-2xl font-semibold">Order not found</h1>
            <Link to="/" className="mt-6 inline-block bg-[#D4AF37] px-7 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-[#1C130B]">Back to Store</Link>
          </div>
        ) : !order ? (
          <p className="text-center text-[#6B5B45]">Loading your order…</p>
        ) : (
          <>
            <div className="text-center">
              {order.status === "paid" ? (
                <CheckCircle2 data-testid="order-status-icon" className="mx-auto mb-4 h-14 w-14 text-[#B08D2B]" />
              ) : (
                <Clock data-testid="order-status-icon" className="mx-auto mb-4 h-14 w-14 text-[#B34A00]" />
              )}
              <h1 className="font-display text-3xl font-semibold sm:text-4xl">
                {order.status === "paid" ? <>Thank You, <span className="text-gold-gradient italic">{order.customer.name.split(" ")[0]}</span></> : "Order Pending"}
              </h1>
              <p className="font-display mt-2 text-base italic text-[#6B5B45]">
                {order.status === "paid"
                  ? "Your order is confirmed. A confirmation has been sent to your email."
                  : "Your payment is not complete yet."}
              </p>
              <p data-testid="order-id" className="mt-4 inline-block rounded-full border border-[#B08D2B]/40 px-5 py-2 font-mono text-sm tracking-wider text-[#8F6E1D]">
                {order.order_id}
              </p>
            </div>
            <ul className="mt-8 space-y-3 border-t border-[#E9DFC8] pt-6">
              {order.items.map((i) => (
                <li key={i.product_id} className="flex items-center gap-3 text-sm">
                  <img src={imgSrc(i.image)} alt={i.name} className="h-12 w-12 rounded-md bg-[#F7F0DF] object-contain p-1" />
                  <span className="flex-1">{i.name} × {i.quantity}</span>
                  <span className="font-semibold text-[#8F6E1D]">{money(order.currency === "USD" ? (i.line_usd ?? Math.round(i.line_inr / 75)) : i.line_inr)}</span>
                </li>
              ))}
            </ul>
            <dl className="mt-6 space-y-1.5 border-t border-[#E9DFC8] pt-4 text-sm text-[#6B5B45]">
              <div className="flex justify-between"><dt>Subtotal</dt><dd>{money(order.subtotal)}</dd></div>
              <div className="flex justify-between"><dt>Shipping</dt><dd className={order.shipping === 0 ? "font-semibold text-green-700" : ""}>{order.shipping === 0 ? "FREE" : money(order.shipping)}</dd></div>
              <div className="flex justify-between text-base font-bold text-[#2B1E12]"><dt>Total Paid</dt><dd data-testid="order-total" className="font-display text-xl text-[#8F6E1D]">{money(order.total)}</dd></div>
            </dl>
            <Link to="/" data-testid="order-continue" className="mt-8 block bg-[#D4AF37] py-3.5 text-center text-[11px] font-bold uppercase tracking-[0.22em] text-[#1C130B] transition-colors hover:bg-[#8F6E1D] hover:text-white">
              Continue Your Sacred Journey
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
